<template>
  <FlashNotifications ref="flash" />

  <!-- ═══════════════ MOBILE ═══════════════ -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-app-screen">

    <!-- Mobile skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse">
        <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
          <div class="flex items-center px-4 h-14">
            <div class="h-5 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
        <div class="pb-20 pt-6 px-4 space-y-6">
          <div class="space-y-3">
            <div class="h-2.5 w-16 bg-gray-200 dark:bg-gray-700 rounded mx-1"></div>
            <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
              <div v-for="i in 6" :key="i" class="px-4 py-3">
                <div class="h-2.5 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                <div class="h-10 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              </div>
            </div>
          </div>
          <div class="space-y-3">
            <div class="h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded mx-1"></div>
            <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
              <div v-for="i in 2" :key="i" class="px-4 py-3">
                <div class="h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                <div class="h-10 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              </div>
            </div>
          </div>
          <div class="space-y-3">
            <div class="h-2.5 w-24 bg-gray-200 dark:bg-gray-700 rounded mx-1"></div>
            <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden p-4">
              <div class="h-9 w-40 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Mobile loaded content -->
    <template v-else>
      <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
        <div class="flex items-center px-4 h-14">
          <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">
            {{ I18N.website_content.account_settings.account_tab.title }}
          </h1>
        </div>
      </div>

      <div class="pb-20 pt-6 px-4 space-y-6">

        <EmailAddressesSettings />

        <!-- Regional -->
        <div>
          <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 px-1">Regional</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
            <div class="px-4 py-3 dark:text-gray-400">
              <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.application.calendar_settings.language }}</p>
              <TimelydoDropdown title="" name="locale" :selected_value="useSettingsStoreObj().current_user.locale" :values="useSettingsStoreObj().languages_list" :opts="languageDropdownOptions" @change="updateAccountPageData" />
            </div>
            <div class="px-4 py-3 dark:text-gray-400">
              <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.application.calendar_settings.country }}</p>
              <TimelydoDropdown title="" name="country_iso_code" :selected_value="useSettingsStoreObj().current_user.country_iso_code" :values="useSettingsStoreObj().countries_list" :opts="countryDropdownOptions" @change="updateAccountPageData" />
            </div>
            <div class="px-4 py-3 dark:text-gray-400">
              <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.website_content.account_settings.account_tab.timezone }}</p>
              <TimelydoDropdown title="" name="timezone" :selected_value="useSettingsStoreObj().current_user.timezone" :values="useSettingsStoreObj().time_zone_list" :opts="timezoneDropdownOptions" @change="updateAccountPageData" />
            </div>
            <div class="px-4 py-3 dark:text-gray-400">
              <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.application.calendar_settings.time_format }}</p>
              <TimelydoDropdown title="" name="time_format" :selected_value="useSettingsStoreObj().current_user.time_format" :values="useSettingsStoreObj().time_formats_list" :opts="{}" @change="updateAccountPageData" />
            </div>
            <div class="px-4 py-3 dark:text-gray-400">
              <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.application.calendar_settings.date_format }}</p>
              <TimelydoDropdown title="" name="date_format" :selected_value="useSettingsStoreObj().current_user.date_format" :values="useSettingsStoreObj().date_formats_json" :opts="{}" @change="updateAccountPageData" />
            </div>
            <div class="px-4 py-3 dark:text-gray-400">
              <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.application.calendar_settings.start_week_on }}</p>
              <TimelydoDropdown title="" name="start_day_of_week" :selected_value="useSettingsStoreObj().current_user.start_day_of_week" :values="useSettingsStoreObj().week_days_list" :opts="{maximum_height: '200px'}" @change="updateAccountPageData" />
            </div>
          </div>
        </div>

        <!-- Personal -->
        <div>
          <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 px-1">Personal</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
            <div class="px-4 py-3">
              <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.website_content.account_settings.account_tab.dob }}</p>
              <v-date-picker v-model="user_dob" :popover="{ visibility: 'focus' }" mode="date" :is-dark="isDark" :locale="CURRENT_LOCALE" :masks="{ input: dobMask }" :max-date="new Date() - (10 * 12 * 30 * 24 * 60 * 60 * 1000)">
                <template #default="{ inputValue, inputEvents }">
                  <input type="text" name="dob" :value="inputValue" v-on="inputEvents" autocomplete="off"
                         :placeholder="dobMask"
                         class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </template>
              </v-date-picker>
            </div>
            <div class="px-4 py-3 dark:text-gray-400">
              <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.website_content.account_settings.account_tab.religion }}</p>
              <TimelydoDropdown title="" name="religion" :selected_value="useSettingsStoreObj().current_user.religion" :values="useSettingsStoreObj().religions_list" :opts="religionDropdownOptions" @change="updateAccountPageData" />
            </div>
          </div>
        </div>

        <!-- Danger zone -->
        <div>
          <p class="text-[11px] font-semibold text-red-400 uppercase tracking-wider mb-2 px-1">{{ I18N.website_content.account_settings.account_tab.danger_zone }}</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden border border-red-100 dark:border-red-900/40 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">{{ I18N.application.onboarding.delete_message }}</p>
            <button @click="toggleModal" type="button"
                    class="flex items-center gap-2 px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-sm font-semibold rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              {{ I18N.website_content.account_settings.delete_account }}
            </button>
          </div>
        </div>

      </div>
    </template>
  </div>

  <!-- ═══════════════ DESKTOP ═══════════════ -->
  <div class="hidden sm:block min-h-app-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- Desktop skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse">
        <div class="sticky top-14 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
          <div class="max-w-5xl mx-auto px-6 flex items-center h-14">
            <div class="flex items-center gap-2.5">
              <div class="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
              <div class="space-y-1.5">
                <div class="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-2.5 w-28 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="max-w-5xl mx-auto px-6 py-6 space-y-4">
          <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
              <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
              <div class="h-3.5 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div class="px-6 py-4 grid grid-cols-2 gap-4">
              <div v-for="i in 6" :key="i" class="space-y-1.5">
                <div class="h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-10 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              </div>
            </div>
          </div>
          <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
              <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
              <div class="h-3.5 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div class="px-6 py-4 grid grid-cols-2 gap-4">
              <div v-for="i in 2" :key="i" class="space-y-1.5">
                <div class="h-2.5 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-10 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              </div>
            </div>
          </div>
          <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
              <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
              <div class="h-3.5 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div class="px-6 py-4">
              <div class="h-2.5 w-64 bg-gray-100 dark:bg-gray-800 rounded mb-4"></div>
              <div class="h-9 w-36 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Desktop loaded content -->
    <template v-else>
      <!-- Sub-page header -->
      <div class="sticky top-14 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
        <div class="max-w-5xl mx-auto px-6 flex items-center h-14">
          <div class="flex items-center justify-between w-full">
            <div class="flex items-center gap-2.5">
              <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
                </svg>
              </div>
              <div>
                <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">{{ I18N.website_content.account_settings.account_tab.title }}</h1>
                <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Preferences and regional settings</p>
              </div>
            </div>
            <SaveStatusPill :status="saveStatus" />
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="max-w-5xl mx-auto px-6 py-6 space-y-4">

        <EmailAddressesSettings />

        <!-- Regional card -->
        <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
          <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
            <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
              <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <div>
              <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Regional</h2>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Language, timezone, and date preferences</p>
            </div>
          </div>
          <div class="px-6 py-4 grid grid-cols-2 gap-x-6 gap-y-4 dark:text-gray-400">
            <div>
              <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.application.calendar_settings.language }}</label>
              <TimelydoDropdown title="" name="locale" :selected_value="useSettingsStoreObj().current_user.locale" :values="useSettingsStoreObj().languages_list" :opts="languageDropdownOptions" @change="updateAccountPageData" />
            </div>
            <div>
              <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.application.calendar_settings.country }}</label>
              <TimelydoDropdown title="" name="country_iso_code" :selected_value="useSettingsStoreObj().current_user.country_iso_code" :values="useSettingsStoreObj().countries_list" :opts="countryDropdownOptions" @change="updateAccountPageData" />
            </div>
            <div>
              <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.website_content.account_settings.account_tab.timezone }}</label>
              <TimelydoDropdown title="" name="timezone" :selected_value="useSettingsStoreObj().current_user.timezone" :values="useSettingsStoreObj().time_zone_list" :opts="timezoneDropdownOptions" @change="updateAccountPageData" />
            </div>
            <div>
              <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.application.calendar_settings.time_format }}</label>
              <TimelydoDropdown title="" name="time_format" :selected_value="useSettingsStoreObj().current_user.time_format" :values="useSettingsStoreObj().time_formats_list" :opts="{}" @change="updateAccountPageData" />
            </div>
            <div>
              <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.application.calendar_settings.date_format }}</label>
              <TimelydoDropdown title="" name="date_format" :selected_value="useSettingsStoreObj().current_user.date_format" :values="useSettingsStoreObj().date_formats_json" :opts="{}" @change="updateAccountPageData" />
            </div>
            <div>
              <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.application.calendar_settings.start_week_on }}</label>
              <TimelydoDropdown title="" name="start_day_of_week" :selected_value="useSettingsStoreObj().current_user.start_day_of_week" :values="useSettingsStoreObj().week_days_list" :opts="{maximum_height: '200px'}" @change="updateAccountPageData" />
            </div>
          </div>
        </div>

        <!-- Personal card -->
        <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
          <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
            <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
              <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <div>
              <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Personal</h2>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Date of birth and other personal details</p>
            </div>
          </div>
          <div class="px-6 py-4 grid grid-cols-2 gap-x-6 gap-y-4 dark:text-gray-400">
            <div>
              <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.website_content.account_settings.account_tab.dob }}</label>
              <v-date-picker v-model="user_dob" :popover="{ visibility: 'focus' }" mode="date" :is-dark="isDark" :locale="CURRENT_LOCALE" :max-date="new Date() - (10 * 12 * 30 * 24 * 60 * 60 * 1000)">
                <template #default="{ inputValue, inputEvents }">
                  <input type="text" name="dob" :value="inputValue" v-on="inputEvents" autocomplete="off"
                         :placeholder="useSettingsStoreObj().current_user.date_format"
                         class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
                </template>
              </v-date-picker>
            </div>
            <div>
              <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">{{ I18N.website_content.account_settings.account_tab.religion }}</label>
              <TimelydoDropdown title="" name="religion" :selected_value="useSettingsStoreObj().current_user.religion" :values="useSettingsStoreObj().religions_list" :opts="religionDropdownOptions" @change="updateAccountPageData" />
            </div>
          </div>
        </div>

        <!-- Danger zone card -->
        <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-red-100 dark:border-red-900/40">
          <div class="px-6 py-4 border-b border-red-100 dark:border-red-900/40 flex items-center gap-2.5">
            <div class="w-6 h-6 rounded-md bg-red-50 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
              <svg class="w-3.5 h-3.5 text-red-500 dark:text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <div>
              <h2 class="text-sm font-semibold text-red-600 dark:text-red-400 leading-none">{{ I18N.website_content.account_settings.account_tab.danger_zone }}</h2>
              <p class="text-[11px] text-red-400/70 dark:text-red-500/70 mt-0.5 leading-none">Permanent and irreversible actions</p>
            </div>
          </div>
          <div class="px-6 py-5">
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">{{ I18N.application.onboarding.delete_message }}</p>
            <button @click="toggleModal" type="button"
                    class="flex items-center gap-2 px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-sm font-semibold rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
              </svg>
              {{ I18N.website_content.account_settings.delete_account }}
            </button>
          </div>
        </div>

      </div>
    </template>
  </div>

  <!-- ═══════════════ DELETE ACCOUNT MODAL ═══════════════ -->
  <div v-show="showModal" class="fixed inset-0 z-40 bg-gray-900/60 backdrop-blur-sm"></div>
  <div v-show="showModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
    <div class="relative w-full max-w-md pointer-events-auto">
      <div class="bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">

        <div class="px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-700 flex items-start justify-between gap-4">
          <div>
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ I18N.application.onboarding.delete_account }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ I18N.application.onboarding.delete_message }}</p>
          </div>
          <button @click="toggleModal" type="button"
                  class="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex-shrink-0">
            <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <form @submit.prevent="deleteAccount">
          <div class="px-6 py-5">
            <TimelydoTextArea :value="delete_reason"
                              @focusout="updateDeleteReason"
                              rows="4"
                              :placeholder="I18N.application.bookings.upcoming.reason"
                              name="user[delete reason]"
                              :opts="{id: 'user_delete_reason'}" />
          </div>
          <div class="px-6 py-4 bg-gray-50 dark:bg-[#0d1117] border-t border-gray-100 dark:border-gray-700 flex gap-3 justify-end">
            <button @click="toggleModal" type="button"
                    class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              {{ I18N.application.bookings.upcoming.cancel }}
            </button>
            <button type="submit"
                    class="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2 min-w-[5rem] justify-center">
              <svg v-if="show_loading_on_account_deletion" class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              <span>{{ show_loading_on_account_deletion ? I18N.application.events.deleting : I18N.application.events.delete }}</span>
            </button>
          </div>
        </form>

      </div>
    </div>
  </div>
</template>

<script>
import {useSettingsStore} from 'controllers/vue_components/settings/v1/store'
import RequestMixin from "controllers/vue_components/mixins/request"
import TimelydoDropdown from "controllers/vue_components/fields/timelydo_dropdown"
import TimelydoTextArea from "controllers/vue_components/fields/timelydo_textarea"
import Constants from "controllers/vue_components/mixins/constants";
import FlashNotifications from "controllers/vue_components/flash_toast"
import SaveStatusPill from "controllers/vue_components/settings/shared/SaveStatusPill"
import EmailAddressesSettings from "controllers/vue_components/settings/v1/email_addresses_settings.vue"

export default {
    name: 'Account',
    props: [],
    components: {TimelydoDropdown, TimelydoTextArea, FlashNotifications, SaveStatusPill, EmailAddressesSettings},
    mixins: [Constants, RequestMixin],
    data() {
        return {
            isDark: document.documentElement.classList.contains('dark'),
            countryDropdownOptions: {
                placeholder: 'Select a country',
                classes: 'custom-dropdown',
                search_able: true,
                maximum_height: '500px',
            },
            languageDropdownOptions: {
                placeholder: 'Select a language',
                classes: 'custom-dropdown',
                search_able: true,
                maximum_height: '300px',
            },
            timezoneDropdownOptions: {
                placeholder: 'Select a timezone',
                classes: 'custom-dropdown',
                search_able: true,
                maximum_height: '300px',
            },
            religionDropdownOptions: {
                placeholder: 'Select Religion',
                classes: 'custom-dropdown',
                search_able: true,
            },
            user_dob: '',
            show_loading_on_account_deletion: false,
            dobFormatMap: {
                '31/12/2024': 'DD/MM/YYYY',
                '12/31/2024': 'MM/DD/YYYY',
                '2023-12-31': 'YYYY-MM-DD',
                '31.12.2024': 'DD.MM.YYYY',
                '2024/12/31': 'YYYY/MM/DD',
                '31-12-2024': 'DD-MM-YYYY',
            },
            showModal: false,
            delete_reason: '',
            saveStatus: 'idle',
        }
    },
    methods: {
        useSettingsStoreObj() {
            return useSettingsStore()
        },

        toggleModal() {
            this.showModal = !this.showModal
        },

        handleError(response) {
            if (response.js_error === false) {
                this.$refs.flash.show(response.response.message, 'alert')
            } else {
                this.$refs.flash.show(response.error?.data?.message || 'Something went wrong', 'alert')
            }
        },

        async updateAccountPageData(value) {
            let attr = Object.keys(value)[0]
            this.saveStatus = 'saving'
            let response = await this.useSettingsStoreObj().updateCurrentUser({ user: value })
            if (response.js_error === false && response.response?.success) {
                this.saveStatus = 'saved'
                setTimeout(() => { if (this.saveStatus === 'saved') this.saveStatus = 'idle' }, 2500)
            } else {
                this.saveStatus = 'idle'
                this.handleError(response)
            }
            if (attr === 'locale') {
                window.location.reload()
            }
        },

        async updateDeleteReason(e) {
            this.delete_reason = e.target.value
        },

        async deleteAccount() {
            if (this.delete_reason.trim() === '') {
                this.$refs.flash.show(this.I18N.notifications.plz_write_reason, 'alert');
            } else {
                this.show_loading_on_account_deletion = true
                let res = await this.axiosRequest('PUT', '/api/v1/user/destroy_me', {user: {delete_reason: this.delete_reason}}, false)
                if (res.js_error == false && res.response.success) {
                    window.location.replace("/")
                } else {
                    this.$refs.flash.show(res.error?.data?.message || res.response?.message || 'Something went wrong', 'alert')
                }
                this.show_loading_on_account_deletion = false
            }
        },
    },

    computed: {
        dobMask() {
            const fmt = this.useSettingsStoreObj().current_user.date_format
            return this.dobFormatMap[fmt] || 'DD/MM/YYYY'
        },
    },

    watch: {
        'user_dob'(newValue) {
            if (newValue !== this.useSettingsStoreObj().current_user.dob) {
                this.updateAccountPageData({dob: newValue})
            }
        },
    },

    created() {
        this.$watch(
            () => this.useSettingsStoreObj().init_data_loaded,
            (loaded) => { if (loaded) this.user_dob = this.useSettingsStoreObj().current_user.dob },
            { immediate: true }
        )
    }
};
</script>
